import { FunctionComponent, ReactNode } from "react";
import { faBullhorn, faEnvelope, faMessages, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { classNames } from "@/utils/classNames";

import { AnalyticsEmptyState } from "../components/AnalyticsEmptyState";
import { AnalyticsSkeleton } from "../components/AnalyticsSkeleton";
import { KpiBreakdown } from "../components/KpiBreakdown";
import { KpiHeroRow } from "../components/KpiHeroRow";
import { KpiRowsSection } from "../sections/KpiRowsSection";
import { CreditsSection } from "../sections/CreditsSection";
import {
	CAMPAIGN_FUNNEL_ROWS,
	EMAIL_ROWS,
	LEADS_ROWS,
	LINKEDIN_ROWS,
	REPLY_ANALYSIS_ROWS,
} from "../config/sections";
import { Analytics, KpiBreakdownBucket, SegmentDimension } from "../schema/analytics";
import { KpiKey } from "../utils/kpiFilterMap";

export interface DeepDiveReportProps {
	data: Analytics | undefined;
	isLoading: boolean;
	/** Narrow layout for a side-by-side column: sections stack instead of pairing up. */
	isCompact?: boolean;
	/**
	 * Rendered directly under the hero tiles. The Deep Dive's "View campaign" / "View leads"
	 * belong there rather than above the report: they act on what the tiles just showed.
	 */
	actions?: ReactNode;
	onKpiClick: (kpi: KpiKey) => void;
	onBucketDrillDown: (dimension: SegmentDimension, bucket: KpiBreakdownBucket) => void;
	onBucketOpenInContacts: (
		dimension: SegmentDimension,
		bucket: KpiBreakdownBucket,
		kpiKey: KpiKey
	) => void;
}

/**
 * The report itself — leads funnel, campaign funnel, credits, reply analysis and the
 * per-channel sections — for one selection. Rendered once on its own, or twice
 * side-by-side when comparing.
 */
export const DeepDiveReport: FunctionComponent<DeepDiveReportProps> = ({
	data,
	isLoading,
	isCompact = false,
	actions,
	onKpiClick,
	onBucketDrillDown,
	onBucketOpenInContacts,
}) => {
	if (isLoading) {
		return <AnalyticsSkeleton />;
	}

	if (!data) {
		return <AnalyticsEmptyState />;
	}

	return (
		<div className="flex flex-col w-full mt-4">
			<KpiHeroRow data={data} onKpiClick={onKpiClick} />

			{/* Held to the hero grid's width so the buttons line up under the four tiles. */}
			{actions ? <div className="w-full max-w-5xl mb-4">{actions}</div> : null}

			<KpiRowsSection
				data={data}
				icon={faUsers}
				labelNamespace="analytics.sections.leads.rows"
				rows={LEADS_ROWS}
				titleKey="analytics.sections.leads.title"
				onKpiClick={onKpiClick}
			/>

			<KpiRowsSection
				data={data}
				icon={faBullhorn}
				labelNamespace="analytics.sections.campaignFunnel.rows"
				rows={CAMPAIGN_FUNNEL_ROWS}
				titleKey="analytics.sections.campaignFunnel.title"
				onKpiClick={onKpiClick}
			/>

			<div
				className={classNames(
					"grid grid-cols-1 gap-0 w-full",
					isCompact ? "" : "lg:grid-cols-2 lg:gap-4 max-w-5xl"
				)}
			>
				<CreditsSection data={data} />
				<KpiRowsSection
					data={data}
					icon={faMessages}
					labelNamespace="analytics.sections.replyAnalysis.rows"
					rows={REPLY_ANALYSIS_ROWS}
					titleKey="analytics.sections.replyAnalysis.title"
					onKpiClick={onKpiClick}
				/>
			</div>

			<KpiRowsSection
				data={data}
				icon={faEnvelope}
				labelNamespace="analytics.sections.email.rows"
				rows={EMAIL_ROWS}
				titleKey="analytics.sections.email.title"
				onKpiClick={onKpiClick}
			/>

			<KpiRowsSection
				data={data}
				icon={faLinkedinIn}
				labelNamespace="analytics.sections.linkedin.rows"
				rows={LINKEDIN_ROWS}
				titleKey="analytics.sections.linkedin.title"
				onKpiClick={onKpiClick}
			/>

			{data.segments ? (
				<KpiBreakdown
					segments={data.segments}
					onBucketDrillDown={onBucketDrillDown}
					onBucketOpenInContacts={onBucketOpenInContacts}
				/>
			) : null}
		</div>
	);
};
